import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { saveAs } from 'file-saver';

import useTranslation from 'customHooks/translate';
import {
  getReportJobNumber,
  getReportJobNumberSummary,
  getReportJobNumberExcell,
  getReportJobNumberPdf,
} from 'services/reports';
import { getReportJobs } from 'services/jobs';
import {
  INVOICE_JOBS,
  REPORTS_EXPENSES,
  REPORTS_INCOMES,
  REPORTS_JOB_NUMBER_SUMMARY,
} from 'events/types';
import CustomDialog from 'pages/Dialogs/CustomDialog';

import { ReportsMultiSelect } from '../components/reportsMultiSelect';
import { ReportsRadio } from '../components/reportsRadio';
import { ReportsTotalViewIncome } from '../components/ReportsTotalViewIncome';
import { ReportsTotalViewExpense } from '../components/ReportsTotalViewExpense';
import { ReportsTab } from '../components/ReportsTab';
import IncomesTable from './IncomesTable';
import ExpensesTable from './ExpensesTable';
import {
  delay,
  getStatusLabel,
  isEmpty,
  openNotification,
  waitForReportCompletion,
} from 'utils';
import { JOB_NUMBER_REPORT_KEY } from 'consts';

export const JobNumber = () => {
  const translation = useTranslation();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState<number[] | 'all'>([2]);
  const [numbers, setNumbers] = useState<any>(null);
  const [shouldFetch, setShouldFetch] = useState(false);

  const [confirmedFromDate, setConfirmedFromDate] = useState('');
  const [confirmedToDate, setConfirmedToDate] = useState('');
  const [confirmedStatus, setConfirmedStatus] = useState<number[] | 'all'>([2]);
  const [confirmedNumbers, setConfirmedNumbers] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'incomes' | 'expenses'>('incomes');
  const [open, setOpen] = useState<boolean>(true);

  const [pageSize] = useState(10);

  // Infinite scroll related states
  const [incomesPageIndex, setIncomesPageIndex] = useState(0);
  const [expensesPageIndex, setExpensesPageIndex] = useState(0);
  const [incomesTableData, setIncomesTableData] = useState<any[]>([]);
  const [expensesTableData, setExpensesTableData] = useState<any[]>([]);
  const [hasMoreIncomes, setHasMoreIncomes] = useState(true);
  const [hasMoreExpenses, setHasMoreExpenses] = useState(true);
  const [isLoadingMoreIncomes, setIsLoadingMoreIncomes] = useState(false);
  const [isLoadingMoreExpenses, setIsLoadingMoreExpenses] = useState(false);

  // Refs for scroll containers and loading states
  const incomesTableContainerRef = useRef<HTMLDivElement>(null);
  const expensesTableContainerRef = useRef<HTMLDivElement>(null);
  const incomesLoadingRef = useRef<HTMLDivElement>(null);
  const expensesLoadingRef = useRef<HTMLDivElement>(null);
  const isFetchingIncomesRef = useRef(false);
  const isFetchingExpensesRef = useRef(false);

  const handleSelect = (values: number[]) => {
    setNumbers(values);
  };

  // Reset data function
  const resetData = () => {
    setIncomesTableData([]);
    setExpensesTableData([]);
    setIncomesPageIndex(0);
    setExpensesPageIndex(0);
    setHasMoreIncomes(true);
    setHasMoreExpenses(true);
  };

  const { data: numbersData } = useQuery<any, AxiosError, any[]>({
    queryKey: [INVOICE_JOBS],
    queryFn: () => getReportJobs(),
    select: (response) =>
      response.data.data.map((d) => ({ ...d, label: d.name, value: d.id })),
  });

  // Function to fetch incomes data
  const fetchIncomesData = useCallback(
    async (currentPage: number, isNewRequest: boolean = false) => {
      if (isFetchingIncomesRef.current) return null;

      isFetchingIncomesRef.current = true;
      setIsLoadingMoreIncomes(true);

      try {
        const response = await getReportJobNumber(
          confirmedFromDate,
          confirmedToDate,
          true,
          confirmedStatus === 'all' ? [] : confirmedStatus,
          confirmedNumbers && confirmedNumbers.length > 0
            ? confirmedNumbers
            : undefined,
          pageSize,
          currentPage
        );

        const data = response.data;

        // Ensure we have data to work with
        if (data?.data?.length) {
          // Set data based on whether this is a new request or not
          if (isNewRequest) {
            setIncomesTableData(data.data);

            // Show notification on initial load
            if (data.data.length === 0 || data.data === null) {
              openNotification(`${translation.reports.NoDataIncome}`);
            } else {
              openNotification(
                `${translation.reports.IncomesDataFound}`,
                'success'
              );
            }
          } else {
            setIncomesTableData((prev) => [...prev, ...data.data]);
          }

          // Check if we have more data to load
          const newTotalItems =
            (isNewRequest ? 0 : incomesTableData.length) + data.data.length;
          setHasMoreIncomes(newTotalItems < data.totalRecord);

          return data;
        }

        if (isNewRequest) {
          openNotification(`${translation.reports.NoDataIncome}`);
        }

        return null;
      } catch {
        return null;
      } finally {
        setIsLoadingMoreIncomes(false);
        isFetchingIncomesRef.current = false;
      }
    },
    [
      confirmedFromDate,
      confirmedToDate,
      confirmedStatus,
      confirmedNumbers,
      pageSize,
      incomesTableData.length,
      translation.reports.NoDataIncome,
      translation.reports.IncomesDataFound,
    ]
  );

  // Function to fetch expenses data
  const fetchExpensesData = useCallback(
    async (currentPage: number, isNewRequest: boolean = false) => {
      if (isFetchingExpensesRef.current) return null;

      isFetchingExpensesRef.current = true;
      setIsLoadingMoreExpenses(true);

      try {
        const response = await getReportJobNumber(
          confirmedFromDate,
          confirmedToDate,
          false,
          confirmedStatus === 'all' ? [] : confirmedStatus,
          confirmedNumbers && confirmedNumbers.length > 0
            ? confirmedNumbers
            : undefined,
          pageSize,
          currentPage
        );

        const data = response.data;

        // Ensure we have data to work with
        if (data?.data?.length) {
          // Set data based on whether this is a new request or not
          if (isNewRequest) {
            setExpensesTableData(data.data);

            // Show notification on initial load
            if (data.data.length === 0 || data.data === null) {
              openNotification(`${translation.reports.NoDataExpense}`);
            } else {
              openNotification(
                `${translation.reports.ExpencesDataFound}`,
                'success'
              );
            }
          } else {
            setExpensesTableData((prev) => [...prev, ...data.data]);
          }

          // Check if we have more data to load
          const newTotalItems =
            (isNewRequest ? 0 : expensesTableData.length) + data.data.length;
          setHasMoreExpenses(newTotalItems < data.totalRecord);

          return data;
        }

        if (isNewRequest) {
          openNotification(`${translation.reports.NoDataExpense}`);
        }

        return null;
      } catch {
        return null;
      } finally {
        setIsLoadingMoreExpenses(false);
        isFetchingExpensesRef.current = false;
      }
    },
    [
      confirmedFromDate,
      confirmedToDate,
      confirmedStatus,
      confirmedNumbers,
      pageSize,
      expensesTableData.length,
      translation.reports.NoDataExpense,
      translation.reports.ExpencesDataFound,
    ]
  );

  useQuery({
    queryKey: [
      REPORTS_INCOMES,
      confirmedFromDate,
      confirmedToDate,
      confirmedStatus,
      confirmedNumbers,
    ],
    queryFn: () => fetchIncomesData(0, true),
    enabled: shouldFetch,
    onSettled: () => {
      setShouldFetch(false);
    },
  });

  useQuery({
    queryKey: [
      REPORTS_EXPENSES,
      confirmedFromDate,
      confirmedToDate,
      confirmedStatus,
      confirmedNumbers,
    ],
    queryFn: () => fetchExpensesData(0, true),
    enabled: shouldFetch,
    onSettled: () => {
      setShouldFetch(false);
    },
  });

  const { data: summaryData } = useQuery({
    queryKey: [REPORTS_JOB_NUMBER_SUMMARY, confirmedStatus, confirmedNumbers],
    queryFn: () =>
      getReportJobNumberSummary(
        confirmedStatus === 'all' ? [] : confirmedStatus,
        confirmedNumbers && confirmedNumbers.length > 0
          ? confirmedNumbers
          : undefined
      ),
    select: (res) => res.data,
    enabled: shouldFetch,
  });

  const handleRadioChange = (value: string) => {
    if (value === translation.reports.Paid) {
      setStatus([2]);
    } else if (value === translation.reports.Unpaid) {
      setStatus([3]);
    } else if (value === translation.reports.All) {
      setStatus('all');
    } else {
      setStatus([]);
    }
  };

  const setDateRange = (from: string, to: string) => {
    setFromDate(from);
    setToDate(to);
  };

  const handleConfirm = () => {
    if (
      !fromDate ||
      !toDate ||
      numbers === null ||
      (status.length === 0 && status !== 'all')
    ) {
      openNotification(`${translation.reports.Parameters}`, 'error');
      return;
    }
    setConfirmedFromDate(fromDate);
    setConfirmedToDate(toDate);
    setConfirmedStatus(status);
    setConfirmedNumbers(numbers);
    resetData();
    setShouldFetch(true);
    setOpen(false);
  };

  const handleExcelExport = async () => {
    try {
      openNotification(
        `${translation.reports.ExcellWait}`,
        'info',
        0,
        JOB_NUMBER_REPORT_KEY,
        true
      );

      const excelResponse = await getReportJobNumberExcell(
        confirmedFromDate,
        confirmedToDate,
        confirmedStatus === 'all' ? [] : confirmedStatus,
        confirmedNumbers
      );

      const reportId = excelResponse?.data?.[0]?.id;

      if (reportId) {
        await delay(4000);

        const downloadResponse = await waitForReportCompletion(
          String(reportId),
          translation
        );
        const blob = new Blob([downloadResponse.data], {
          type: 'application/vnd.ms-excel',
        });
        saveAs(blob, `report_${reportId}.xlsx`);

        openNotification(
          `${translation.reports.ExcellGenerated}`,
          'success',
          2,
          JOB_NUMBER_REPORT_KEY
        );
      } else {
        openNotification(
          `${translation.reports.Failed}`,
          'error',
          2,
          JOB_NUMBER_REPORT_KEY
        );
      }
    } catch (error) {
      openNotification(
        `${translation.reports.Failed}`,
        'error',
        2,
        JOB_NUMBER_REPORT_KEY
      );
    }
  };

  const handlePdfExport = async () => {
    try {
      openNotification(
        `${translation.reports.PdfWait}`,
        'info',
        0,
        JOB_NUMBER_REPORT_KEY,
        true
      );

      const excelResponse = await getReportJobNumberPdf(
        confirmedFromDate,
        confirmedToDate,
        confirmedStatus,
        confirmedNumbers
      );

      const reportId = excelResponse?.data?.[0]?.id;

      if (reportId) {
        await delay(4000);

        const downloadResponse = await waitForReportCompletion(
          String(reportId),
          translation
        );
        const blob = new Blob([downloadResponse.data], {
          type: 'application/pdf',
        });
        saveAs(blob, `report_${reportId}.pdf`);

        openNotification(
          `${translation.reports.PdfGenerated}`,
          'success',
          2,
          JOB_NUMBER_REPORT_KEY
        );
      } else {
        openNotification(
          `${translation.reports.Failed}`,
          'error',
          2,
          JOB_NUMBER_REPORT_KEY
        );
      }
    } catch (error) {
      openNotification(
        `${translation.reports.Failed}`,
        'error',
        2,
        JOB_NUMBER_REPORT_KEY
      );
    }
  };

  // Handle incomes scroll
  const handleIncomesScroll = useCallback(() => {
    if (
      !incomesTableContainerRef.current ||
      !incomesLoadingRef.current ||
      isFetchingIncomesRef.current ||
      !hasMoreIncomes
    )
      return;

    const containerRect =
      incomesTableContainerRef.current.getBoundingClientRect();
    const loadingRect = incomesLoadingRef.current.getBoundingClientRect();

    // Check if the loading element is visible
    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = incomesPageIndex + 1;
      setIncomesPageIndex(nextPage);
      fetchIncomesData(nextPage);
    }
  }, [incomesPageIndex, hasMoreIncomes, fetchIncomesData]);

  // Handle expenses scroll
  const handleExpensesScroll = useCallback(() => {
    if (
      !expensesTableContainerRef.current ||
      !expensesLoadingRef.current ||
      isFetchingExpensesRef.current ||
      !hasMoreExpenses
    )
      return;

    const containerRect =
      expensesTableContainerRef.current.getBoundingClientRect();
    const loadingRect = expensesLoadingRef.current.getBoundingClientRect();

    // Check if the loading element is visible
    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = expensesPageIndex + 1;
      setExpensesPageIndex(nextPage);
      fetchExpensesData(nextPage);
    }
  }, [expensesPageIndex, hasMoreExpenses, fetchExpensesData]);

  // Set up scroll event listeners
  useEffect(() => {
    const incomesContainer = incomesTableContainerRef.current;
    if (incomesContainer) {
      incomesContainer.addEventListener('scroll', handleIncomesScroll);
      return () => {
        incomesContainer.removeEventListener('scroll', handleIncomesScroll);
      };
    }
  }, [handleIncomesScroll]);

  useEffect(() => {
    const expensesContainer = expensesTableContainerRef.current;
    if (expensesContainer && activeTab === 'expenses') {
      expensesContainer.addEventListener('scroll', handleExpensesScroll);
      handleExpensesScroll();
      return () => {
        expensesContainer.removeEventListener('scroll', handleExpensesScroll);
      };
    }
  }, [handleExpensesScroll, activeTab]);

  const isIncomesSelected = activeTab === 'incomes';
  const isShowButtons =
    incomesTableData.length > 0 || expensesTableData.length > 0;

  return (
    <div className="grid h-full grid-cols-1 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
      <div className="col-span-4 mb-[20px] md:mb-[0px]">
        {(confirmedFromDate ||
          confirmedToDate ||
          confirmedStatus ||
          confirmedNumbers) && (
          <div className="my-[12px] px-[16px] md:px-[32px]">
            <div className="flex flex-wrap items-center gap-[8px] rounded-[8px] bg-[#F6F6F6] px-[12px] py-[8px] text-[12px] text-[#4B4B4B] md:text-[13px]">
              <span className="font-semibold text-[#1D1D1D]">
                {translation.filter}:
              </span>
              {confirmedFromDate && confirmedToDate && (
                <span className="rounded-[999px] bg-white px-[10px] py-[4px]">
                  {confirmedFromDate} - {confirmedToDate}
                </span>
              )}
              <span className="rounded-[999px] bg-white px-[10px] py-[4px]">
                <span className="font-semibold">
                  {translation.reports.Status}:{' '}
                </span>
                {getStatusLabel(confirmedStatus, translation)}
              </span>
              <span className="rounded-[999px] bg-white px-[10px] py-[4px]">
                <span className="font-semibold">
                  {translation.reports.JobNumber} :{' '}
                </span>
                {isEmpty(numbers)
                  ? translation.reports.All
                  : numbersData
                      ?.filter((option) => numbers?.includes(option.value))
                      .map((option) => option.label)
                      .join(', ')}
              </span>
            </div>
          </div>
        )}
        {isIncomesSelected ? (
          <IncomesTable
            incomesTableContainerRef={incomesTableContainerRef}
            incomesTableData={incomesTableData}
            incomesLoadingRef={incomesLoadingRef}
            isLoadingMoreIncomes={isLoadingMoreIncomes}
            hasMoreIncomes={hasMoreIncomes}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            setOpen={setOpen}
          />
        ) : (
          <ExpensesTable
            expensesTableContainerRef={expensesTableContainerRef}
            expensesTableData={expensesTableData}
            expensesLoadingRef={expensesLoadingRef}
            isLoadingMoreExpenses={isLoadingMoreExpenses}
            hasMoreExpenses={hasMoreExpenses}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            setOpen={setOpen}
          />
        )}
      </div>
      {isShowButtons && (
        <div className="h-full  md:border-l md:border-l-[#f5f7f9]">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[12px] px-[16px] md:pt-[12px]">
            <button
              onClick={handleExcelExport}
              className="flex w-full items-center gap-[8px] rounded-[6px] rounded-[6px] border-2  border-[#CECECE] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:px-[24px] md:py-[12px]"
            >
              <img
                className="mr-[5px] h-5 w-5"
                src="./assets/save-folder-icon.png"
                alt=""
              />
              <p>{translation.reports.SaveAsExcel}</p>
            </button>
            <button
              onClick={handlePdfExport}
              className="flex w-full items-center gap-[8px] rounded-[6px] rounded-[6px] border-2 border-[#CECECE] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:px-[24px] md:py-[12px]"
            >
              <img
                className="mr-[5px] h-5 w-5"
                src="./assets/save-folder-icon.png"
                alt=""
              />
              <p>{translation.reports.SaveAsPdf}</p>
            </button>
          </div>
          {isIncomesSelected
            ? incomesTableData.length > 0 && (
                <ReportsTotalViewIncome
                  title={translation.reports.SummaryIncome}
                  summaryData={summaryData}
                />
              )
            : expensesTableData.length > 0 && (
                <ReportsTotalViewExpense
                  title={translation.reports.SummaryExpence}
                  summaryData={summaryData}
                />
              )}
        </div>
      )}

      <CustomDialog
        open={open}
        handleClose={() => {
          setOpen(false);
        }}
        title={translation.filter}
        buttonTitle={translation.reports.BtnConfirm}
        onSubmit={handleConfirm}
      >
        <ReportsTab setDateRange={setDateRange} />
        <ReportsMultiSelect
          title={translation.reports.ChooseJobNumber}
          onChange={handleSelect}
          options={numbersData || []}
        />
        <ReportsRadio
          title={translation.reports.Status}
          valueOne={translation.reports.Paid}
          valueTwo={translation.reports.Unpaid}
          valueThree={translation.reports.All}
          onChange={handleRadioChange}
          defaultValue=""
        />
      </CustomDialog>
    </div>
  );
};

export default JobNumber;
